import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  LoadScript,
  Marker,
  OverlayView,
} from "@react-google-maps/api";
import styled from "styled-components";
import Text from "../components/text";
import { ClapSpinner } from 'react-spinners-kit';

const MapWrapper = styled.div`
  height: 500px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const InfoWrapper = styled.div`
  display: inline-flex;
  position: absolute;
  background-color: ${(props) => props.theme.colors.white};
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0px 1px 4px -1px ${(props) => props.theme.colors.primary};
  width: max-content;
  transform: translate(-50%);
  bottom: 50px;
  z-index: 1;
  white-space: pre-wrap;
`;

const LoadingMapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LoadingMap = ({labelColour}: labelColour) => (
  <LoadingMapWrapper>
    <Text fontStyle="body" color={labelColour} paddingBottom={3}>
      Loading map
    </Text>
    <ClapSpinner
      aria-busy
      size={30}
      frontColor={labelColour?.toString()}
      backColor={labelColour?.toString()}
    />
  </LoadingMapWrapper>
);

type labelColour = {
  labelColour: string | string [] | null;
}

export type MapMarker = {
  key: string;
  lat: number;
  lng: number;
  info: string;
};

type GoogleMapProps = {
  markers: MapMarker[];
  selectedMarker?: MapMarker;
  onMarkerClick: (markerKey: string) => void;
  labelColour?: string | string[] | null;
};

const Map = ({ markers, selectedMarker, onMarkerClick, labelColour }: GoogleMapProps) => {
  const googleMapsApiKey = process.env.GATSBY_GOOGLE_MAPS_API_KEY!;
  const [googleMap, setGoogleMap] = useState<google.maps.Map<Element> | null>(
    null
  );
  labelColour = labelColour ?? 'black';

  useEffect(() => {
    if (googleMap) {
      if (googleMapsApiKey && markers.length > 0 && selectedMarker) {
        const { lat, lng } = selectedMarker;
        googleMap?.panTo({ lat, lng });

        const bounds = new google.maps.LatLngBounds();
        bounds.extend(new google.maps.LatLng(lat, lng))
        googleMap.fitBounds(bounds, 1000);
        googleMap.setZoom(13);
      }
    }
  }, [googleMapsApiKey, googleMap, markers, selectedMarker]);

  return (
    <MapWrapper>
      {googleMapsApiKey && markers.length > 0 ? (
        <LoadScript
          googleMapsApiKey={googleMapsApiKey}
          loadingElement={
            <LoadingMap labelColour={labelColour}/>
          }
        >
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%" }}
            onLoad={(map: google.maps.Map<Element>) => {
              const bounds = new google.maps.LatLngBounds();
              markers.forEach(({ lat, lng }) =>
                bounds.extend(new google.maps.LatLng(lat, lng))
              );
              map.fitBounds(bounds);
              setGoogleMap(map);
            }}
            options={{
              styles: [
                {
                  featureType: "poi",
                  stylers: [{ visibility: "off" }],
                },
              ],
            }}
            onUnmount={() => setGoogleMap(null)}
          >
            {markers.map(({ key, lat, lng }) => (
              <Marker
                key={key}
                position={{ lat, lng }}
                onClick={() => {
                  googleMap?.panTo({ lat, lng });
                  onMarkerClick(key);
                }}
              />
            ))}
            {selectedMarker && (
              <OverlayView
                position={{
                  lat: selectedMarker.lat,
                  lng: selectedMarker.lng,
                }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <InfoWrapper>
                  <Text fontStyle="body">{selectedMarker.info}</Text>
                </InfoWrapper>
              </OverlayView>
            )}
          </GoogleMap>
        </LoadScript>
      ) : (
        <LoadingMap labelColour={labelColour} />
      )}
    </MapWrapper>
  );
};

export default Map;
